import { useState, useCallback } from 'react';
import { message } from 'antd';
import { Task } from './types';

export const useTranslationApi = (username: string) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // 获取任务列表
  const fetchTasks = useCallback(async (currentPage: number = page, currentPageSize: number = pageSize) => {
    if (!username) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/translation/tasks?username=${username}&page=${currentPage}&page_size=${currentPageSize}`);
      if (!res.ok) {
        message.error(`获取任务列表失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const data = await res.json();
      setTasks(data.tasks || []);
      setTotal(data.total || 0);
      setPage(currentPage);
      setPageSize(currentPageSize);
    } catch (e) {
      message.error('获取任务列表失败');
      console.error('Fetch tasks error:', e);
    } finally {
      setLoading(false);
    }
  }, [username, page, pageSize]);

  // 下载结果文件
  const handleDownload = async (task_id: string) => {
    if (!username) {
      message.error('用户未登录');
      return;
    }
    
    try {
      const res = await fetch(`/translation/tasks/${task_id}/download?username=${username}`);
      if (!res.ok) {
        message.error(`下载失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translation_result_${task_id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('下载成功');
    } catch (e) {
      message.error('下载失败');
      console.error('Download error:', e);
    }
  };

  // 重试任务
  const handleRetry = async (task_id: string) => {
    if (!username) {
      message.error('用户未登录');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('username', username);
      
      const res = await fetch(`/translation/tasks/${task_id}/retry`, {
        method: 'POST',
        body: formData,
      });
      
      if (!res.ok) {
        message.error(`重试失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const data = await res.json();
      if (data.message) {
        message.success('任务重试已开始');
        // 重试后刷新数据
        fetchTasks(page, pageSize);
      } else {
        message.error('重试失败');
      }
    } catch (e) {
      message.error('重试失败');
      console.error('Retry task error:', e);
    }
  };

  // 编辑重试任务
  const handleEditRetry = async (task_id: string, formData: FormData) => {
    if (!username) {
      message.error('用户未登录');
      return false;
    }
    
    setSubmitLoading(true);
    try {
      formData.append('username', username);
      console.log("开始编辑重试翻译任务:", formData);
      
      const res = await fetch(`/translation/tasks/${task_id}/edit_retry`, {
        method: 'POST',
        body: formData,
      });
      
      if (!res.ok) {
        message.error(`编辑重试失败: ${res.status} ${res.statusText}`);
        return false;
      }
      
      let data: any = {};
      try {
        data = await res.json();
      } catch (e) {
        message.error('服务端返回格式错误');
        return false;
      }
      
      console.log("API响应数据:", data);
      console.log("检查task_id:", data.task_id);
      if (data.task_id || data.id) {
        message.success('任务编辑重试成功！');
        fetchTasks(page, pageSize);
        return true;
      } else {
        message.error('任务编辑重试失败');
        return false;
      }
    } catch (e) {
      message.error('网络异常或表单校验失败');
      console.log('表单校验或网络异常:', e);
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  // 提交翻译任务
  const handleSubmit = async (formData: FormData) => {
    if (!username) {
      message.error('用户未登录');
      return false;
    }
    
    setSubmitLoading(true);
    try {
      formData.append('username', username);
      console.log("开始提交翻译任务:", formData);
      
      const res = await fetch('/translation/tasks', {
        method: 'POST',
        body: formData,
      });
      
      if (!res.ok) {
        message.error(`任务提交失败: ${res.status} ${res.statusText}`);
        return false;
      }
      
      let data: any = {};
      try {
        data = await res.json();
      } catch (e) {
        message.error('服务端返回格式错误');
        return false;
      }
      
      console.log("API响应数据:", data);
      console.log("检查task_id:", data.task_id);
      if (data.task_id || data.id) {
        message.success('任务提交成功！任务已进入后台处理队列，请稍后查看处理进度。');
        fetchTasks(page, pageSize);
        return true;
      } else {
        message.error('任务提交失败');
        return false;
      }
    } catch (e) {
      message.error('网络异常或表单校验失败');
      console.log('表单校验或网络异常:', e);
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  return {
    tasks,
    total,
    page,
    pageSize,
    loading,
    submitLoading,
    fetchTasks,
    handleDownload,
    handleRetry,
    handleEditRetry,
    handleSubmit
  };
};
